import React, {useEffect, useContext} from "react";
import {Typography, makeStyles, Box, Link, Grid} from "@material-ui/core";
import PropTypes from "prop-types";
import {useResortDetailLazy, useResortContactDetails} from "../../../hooks";
import {LocaleContext} from "../../../store/providers/LocaleProvider";
import {Skeleton} from "@material-ui/lab";
import {hexToRgb} from "../../../common/theme";
import {retrieveContentfulImage} from "../../../common/image";
import clsx from "clsx";
import {useLocalizedUrl, useI18N} from "../../../common/common";
import {parseWeekdays} from "../../../common/date";
import {enDash} from "../../../common/constant";
import TableContainer from "@material-ui/core/TableContainer";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import TableBody from "@material-ui/core/TableBody";
import {HOME} from "../../../common/routes";

const useStyles = makeStyles((theme) => ({
    countryDetails: {
        marginBottom: theme.spacing(5),
        color: hexToRgb(theme.palette.black, theme.palette.textOpacity.textHigh)
    },
    hostImage: {
        width: theme.spacing(10),
        height: theme.spacing(10),
        borderRadius: theme.spacing(10),
    },
    mb1: {
        marginBottom: theme.spacing(1)
    },
    mb2: {
        marginBottom: theme.spacing(2)
    },
    mb3: {
        marginBottom: theme.spacing(3)
    },
    telephoneLink: {
        color: hexToRgb(theme.palette.black, theme.palette.textOpacity.textHigh),
        ...theme.typography.infoMessage,
        "&:hover": {
            textDecoration: "none"
        }
    },
    resortLink: {
        color: hexToRgb(theme.palette.black, theme.palette.textOpacity.textHigh),
        textDecorationLine: "underline"
    },
    tableRoot: {
        border: "unset",
        boxShadow: "unset",
        width: "50%",
        margin: "auto",
        marginBottom: theme.spacing(3),
        [theme.breakpoints.down("xs")]: {
            width: "100%",
        }
    },
    tableCell: {
        paddingTop: 0,
        paddingRight: 0,
        paddingBottom: theme.spacing(0.5),
        border: "unset",
        [theme.breakpoints.up("md")]: {
            paddingBottom: 0,
        }
    },
    tableCellFirst: {
        paddingRight: 0,
        verticalAlign: "top",
        whiteSpace: "nowrap",
        color: hexToRgb(theme.palette.black, theme.palette.textOpacity.textHigh),
    },
    tableCellRight: {
        color: hexToRgb(theme.palette.black, theme.palette.textOpacity.textMedium),
        whiteSpace: "pre-line"
    },
    [theme.breakpoints.down("xs")]: {
        whiteSpace: "nowrap"
    }
}));

const CallUsResortContactDetails = ({resortId}) => {
    const classes = useStyles();
    const {getResortDetails, resortDetails, loadingResortDetails} = useResortDetailLazy();
    const {getResortContactDetails, resortContactDetails, loading} = useResortContactDetails();
    const {localeUppercase, locale} = useContext(LocaleContext)[0];
    const {getLocalizedUrl} = useLocalizedUrl();
    const {i18n} = useI18N();

    const isLoading = loading || loadingResortDetails;

    useEffect(() => {
        const queryVariables = {variables: {hapimagResortId: resortId, locale: localeUppercase}};
        getResortDetails(queryVariables);
        getResortContactDetails(queryVariables);
    }, []);

    const openTimeDisplay = ((openingHoursMorningFrom, openingHoursMorningTo, openingHoursAfternoonFrom, openingHoursAfternoonTo) => {

        const morningDaysAreSet = openingHoursMorningFrom && openingHoursMorningTo;
        const afternoonDaysAreSet = openingHoursAfternoonFrom && openingHoursAfternoonTo;
        let response = "";

        // No dates are set
        if (!morningDaysAreSet && !afternoonDaysAreSet) {
            return i18n("view.resort.good_to_know.closed");
        }

        // Morning dates are set
        if (morningDaysAreSet) {
            response += getDashSeparatedTimes(openingHoursMorningFrom, openingHoursMorningTo);

            if (afternoonDaysAreSet) {
                response += "\n";
            }
        }

        // Afternoon dates are set
        if (afternoonDaysAreSet) {
            response += getDashSeparatedTimes(openingHoursAfternoonFrom, openingHoursAfternoonTo);
        }
        return response;
    });


    const getDashSeparatedTimes = ((time1, time2) => {
        return `${time1} ${enDash} ${time2}`;
    });

    return (
        <Box
            align="center"
        >
            <Typography
                variant="h3"
                className={classes.mb2}
            >
                {isLoading ? <Skeleton /> : resortDetails?.resortName}
            </Typography>

            <Typography
                variant="body1"
                className={classes.countryDetails}
            >
                {isLoading ? <Skeleton /> : resortDetails?.destination?.country?.country}
            </Typography>

            <Box mb={3}>
                {resortContactDetails?.host?.picture?.url && (
                    <>
                        <img
                            src={retrieveContentfulImage(resortContactDetails.host?.picture?.url, {width: 160, height: 160, fit: "thumb"})}
                            alt={resortContactDetails?.host?.firstAndLastName + ", " + resortContactDetails?.host?.role}
                            className={clsx(classes.hostImage, classes.mb3)}
                        />
                    </>
                )}
            </Box>

            <Typography
                variant="subtitle2"
                className={classes.mb3}
            >
                {isLoading ? <Skeleton /> : resortDetails?.resortContactDetails?.host?.firstAndLastName + ", " + resortDetails?.resortContactDetails?.host?.role}
            </Typography>

            <Typography
                variant="body2"
                className={classes.mb1}
            >
                {isLoading ? <Skeleton /> : i18n("dialog.contact.calls_us.resort.calling_hours")}
            </Typography>

            <Grid
                item
                xs={12}
                container
            >
                <TableContainer
                    component={Paper}
                    classes={{root: classes.tableRoot}}
                >
                    <Table>
                        <TableBody>
                            {resortDetails?.resortInfo?.openingHoursCollection?.items?.filter(i => i !== null || undefined).map((item, index) => (
                                <TableRow key={index}>
                                    <TableCell
                                        className={clsx(classes.tableCell, classes.tableCellFirst)}
                                        align="right"
                                    >
                                        <Typography
                                            variant={"body2"}
                                        >
                                            {item.weekdays && (parseWeekdays(item.weekdays, locale, i18n("view.resort.good_to_know.every_day")) + ":")}
                                        </Typography>
                                    </TableCell>

                                    <TableCell className={classes.tableCell}>
                                        <Typography
                                            variant={"body2"}
                                            className={classes.tableCellRight}
                                        >
                                            {openTimeDisplay(item.openingHoursMorningFrom, item.openingHoursMorningTo, item.openingHoursAfternoonFrom, item.openingHoursAfternoonTo, item.allDayOpen24H)}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Grid>

            <Box
                mb={3}
            >
                {resortContactDetails?.resortPhoneNumber &&
                    <Link
                        href={`tel:${resortContactDetails?.resortPhoneNumber}`}
                        className={classes.telephoneLink}
                    >
                        {isLoading ? <Skeleton /> : resortContactDetails?.resortPhoneNumber}
                    </Link>
                }

            </Box>

            <Box
                mb={3}
            >
                {resortContactDetails?.resortEmailAddress &&
                    <Link
                        href={`mailto:${resortContactDetails?.resortEmailAddress}`}
                        variant="body1"
                    >
                        {isLoading ? <Skeleton /> : resortContactDetails?.resortEmailAddress}
                    </Link>
                }
            </Box>

            <Box
                mb={3}
            >
                <Link
                    variant={"body1"}
                    href={getLocalizedUrl(HOME + resortDetails?.destination?.country?.slug + "/" + resortDetails?.destination?.slug + "/" + resortDetails?.slug)}
                    className={classes.resortLink}
                >
                    {isLoading ? <Skeleton /> : i18n("view.reservations.see_resort_page")}
                </Link>
            </Box>

        </Box>

    );

};

CallUsResortContactDetails.propTypes = {
    resortId: PropTypes.number
};


export default CallUsResortContactDetails;
